import { useBusinessContext } from '@root/context'
import { useQuery } from '@tanstack/react-query'
import {
  fetchWorkflow,
  fetchWorkflowEventTriggerTypes,
  fetchWorkflowFilterTypes,
  fetchWorkflowNodeTypes,
  fetchWorkflowRun,
  fetchWorkflowRuns,
  fetchWorkflowScheduledTriggerFrequencies,
  fetchWorkflows,
  IWorkflowRun
} from '../workflows'

export const queryKeyForWorkflows = (slug: string, ordering?: string) =>
  ordering ? [slug, 'workflows', ordering] : [slug, 'workflows']

export const queryKeyForWorkflow = (slug: string, workflowId: number) => [
  slug,
  'workflows',
  workflowId
]

export const RUN_POLL_INTERVAL_MS = 10000

export const isWorkflowRunLive = (run: Pick<IWorkflowRun, 'status'>): boolean =>
  run.status === 'pending' || run.status === 'running'

export const queryKeyForWorkflowRuns = (slug: string, workflowId: number, page = 1) => [
  slug,
  'workflows',
  workflowId,
  'runs',
  'list',
  page
]

export const queryKeyForWorkflowRun = (slug: string, workflowId: number, runId: number) => [
  slug,
  'workflows',
  workflowId,
  'runs',
  'detail',
  runId
]

export const queryKeyForWorkflowNodeTypes = (slug: string) => [
  slug,
  'constants',
  'workflow_node_types'
]

export const queryKeyForWorkflowEventTriggerTypes = (slug: string) => [
  slug,
  'constants',
  'workflow_event_trigger_types'
]

export const queryKeyForWorkflowScheduledTriggerFrequencies = (slug: string) => [
  slug,
  'constants',
  'workflow_scheduled_trigger_frequencies'
]

export const queryKeyForWorkflowFilterTypes = (slug: string) => [
  slug,
  'constants',
  'workflow_filter_types'
]

export const useWorkflowEventTriggerTypes = () => {
  const { slug } = useBusinessContext()

  return useQuery({
    queryKey: queryKeyForWorkflowEventTriggerTypes(slug),
    queryFn: () => fetchWorkflowEventTriggerTypes({ slug }),
    staleTime: 60 * 60 * 1000
  })
}

export const useWorkflowScheduledTriggerFrequencies = () => {
  const { slug } = useBusinessContext()

  return useQuery({
    queryKey: queryKeyForWorkflowScheduledTriggerFrequencies(slug),
    queryFn: () => fetchWorkflowScheduledTriggerFrequencies({ slug }),
    staleTime: 60 * 60 * 1000
  })
}

export const useWorkflows = (ordering?: string) => {
  const { slug } = useBusinessContext()

  return useQuery({
    queryKey: queryKeyForWorkflows(slug, ordering),
    queryFn: () => fetchWorkflows({ slug }, ordering ? { ordering } : undefined),
    staleTime: 60 * 1000
  })
}

export const useWorkflow = (workflowId: number) => {
  const { slug } = useBusinessContext()

  return useQuery({
    queryKey: queryKeyForWorkflow(slug, workflowId),
    queryFn: () => fetchWorkflow({ slug, workflowId }),
    enabled: Number.isFinite(workflowId)
  })
}

export const useWorkflowNodeTypes = () => {
  const { slug } = useBusinessContext()

  return useQuery({
    queryKey: queryKeyForWorkflowNodeTypes(slug),
    queryFn: () => fetchWorkflowNodeTypes({ slug }),
    // 1 hour — enum-backed constants; safe to cache aggressively across builder visits.
    staleTime: 60 * 60 * 1000
  })
}

export const useWorkflowFilterTypes = () => {
  const { slug } = useBusinessContext()

  return useQuery({
    queryKey: queryKeyForWorkflowFilterTypes(slug),
    queryFn: () => fetchWorkflowFilterTypes({ slug }),
    staleTime: 60 * 60 * 1000
  })
}

export const useWorkflowRuns = (workflowId: number, page: number, enabled = true) => {
  const { slug } = useBusinessContext()

  return useQuery({
    queryKey: queryKeyForWorkflowRuns(slug, workflowId, page),
    queryFn: () => fetchWorkflowRuns({ slug, workflowId }, { page }),
    enabled: enabled && Number.isFinite(workflowId),
    staleTime: RUN_POLL_INTERVAL_MS,
    refetchInterval: query => {
      const results = query.state.data?.results
      if (!results?.some(isWorkflowRunLive)) {
        return false
      }
      return RUN_POLL_INTERVAL_MS
    }
  })
}

export const useWorkflowRun = (workflowId: number, runId: number | null, enabled = true) => {
  const { slug } = useBusinessContext()

  return useQuery({
    queryKey: queryKeyForWorkflowRun(slug, workflowId, runId ?? 0),
    queryFn: () => fetchWorkflowRun({ slug, workflowId, runId: runId as number }),
    enabled: enabled && Number.isFinite(workflowId) && Number.isFinite(runId),
    staleTime: RUN_POLL_INTERVAL_MS,
    refetchInterval: query => {
      const run = query.state.data
      if (!run || !isWorkflowRunLive(run)) {
        return false
      }
      return RUN_POLL_INTERVAL_MS
    }
  })
}
